/**
 * src/lib/api/cfo.js — CFO layer client API
 */

import { apiFetch, API_BASE } from './client';
import { bytesToBase64 } from '../utils/base64';

const CFO = `${API_BASE}/data`;

// ── Fetch entities ──────────────────────────────────────────────────

/**
 * Paginate through all invoices (100 per page) until a page has < 100 rows.
 * The server handler returns PAGE_SIZE=100, so we loop until we get a partial page.
 *
 * TICKET-990: a page FAILURE (apiFetch returns null on network/timeout, or
 * {success:false} on a non-2xx GET response) must not be read as "pagination
 * ended" — `data?.invoices || []` treated both the same as an empty page and
 * silently truncated the ledger. Throw instead so the caller sees the failure.
 */
export async function fetchCFOInvoices() {
  const all = [];
  let page = 0;
  const maxPages = 50; // defensive cap to prevent infinite loops

  while (page <= maxPages) {
    const url = page === 0
      ? `${CFO}?action=cfo.entity&entity=invoices`
      : `${CFO}?action=cfo.entity&entity=invoices&page=${page}`;
    const data = await apiFetch(url);
    if (!data || data.success === false) {
      throw new Error(`Failed to fetch invoices page ${page}${data?.error ? `: ${data.error}` : ''}`);
    }
    const invoices = data?.invoices || [];

    if (invoices.length === 0) break;
    all.push(...invoices);

    if (invoices.length < 100) break;
    page++;
  }

  return all;
}

// CFO Cost-Intelligence dashboard (P4) — cost-tagged invoice ledger aggregates
// (totals, monthly fixed/variable, by-center, by-category, budget variance).
// CEO/admin only; non-privileged callers get {records:[],count:0} (handled here).
export async function fetchCostDashboard() {
  const data = await apiFetch(`${CFO}?action=cfo.cost_dashboard`);
  return data?.ok ? data : null;
}

// Per-project P&L (Slice 3): revenue - material(est) - labor - overhead.
// CEO/admin only; non-privileged callers get {records:[],count:0} -> [].
export async function fetchProjectMargin(projectId) {
  const qs = projectId ? `&project_id=${encodeURIComponent(projectId)}` : '';
  const data = await apiFetch(`${CFO}?action=cfo.project_margin${qs}`);
  return data?.ok ? (data.rows || []) : [];
}

/**
 * Paginate through all supplier invoices (100 per page) until a page has < 100 rows.
 *
 * TICKET-990: same page-failure guard as fetchCFOInvoices — don't mistake a
 * failed page for the legitimate end of pagination.
 */
export async function fetchSupplierInvoicesCFO() {
  const all = [];
  let page = 0;
  const maxPages = 50; // defensive cap to prevent infinite loops

  while (page <= maxPages) {
    const url = page === 0
      ? `${CFO}?action=cfo.entity&entity=supplier_invoices`
      : `${CFO}?action=cfo.entity&entity=supplier_invoices&page=${page}`;
    const data = await apiFetch(url);
    if (!data || data.success === false) {
      throw new Error(`Failed to fetch supplier invoices page ${page}${data?.error ? `: ${data.error}` : ''}`);
    }
    const supplierInvoices = data?.supplier_invoices || [];

    if (supplierInvoices.length === 0) break;
    all.push(...supplierInvoices);

    if (supplierInvoices.length < 100) break;
    page++;
  }

  return all;
}

export async function fetchBankTransactions() {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=bank_transactions`);
  return data?.bank_transactions || [];
}

export async function fetchDealForecasts() {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=deal_forecasts`);
  return data?.deal_forecasts || [];
}

export async function fetchChangeOrders(projectId) {
  const url = projectId
    ? `${CFO}?action=cfo.entity&entity=change_orders&project_id=${encodeURIComponent(projectId)}`
    : `${CFO}?action=cfo.entity&entity=change_orders`;
  const data = await apiFetch(url);
  return data?.change_orders || [];
}

// ── Invoice CRUD ────────────────────────────────────────────────────

export async function upsertInvoice(invoice) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.upsert_invoice', ...invoice }),
  });
}

export async function deleteInvoice(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.delete_invoice', id }),
  });
}

// ── Supplier Invoice CRUD ───────────────────────────────────────────

export async function upsertSupplierInvoice(invoice) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.upsert_supplier_invoice', ...invoice }),
  });
}

export async function deleteSupplierInvoice(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.delete_supplier_invoice', id }),
  });
}

// ── Bank CSV ────────────────────────────────────────────────────────

export async function importBankCSV(bank, transactions) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.import_bank_csv', bank, transactions }),
  });
}

export async function matchTransaction(transactionId, invoiceId, supplierInvoiceId) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({
      action: 'cfo.match_transaction',
      id: transactionId,
      matched_invoice_id: invoiceId || undefined,
      matched_supplier_invoice_id: supplierInvoiceId || undefined,
    }),
  });
}

export async function unmatchTransaction(transactionId) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.unmatch_transaction', id: transactionId }),
  });
}

// ── Deal Forecasts ──────────────────────────────────────────────────

export async function upsertDealForecast(forecast) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.upsert_deal_forecast', ...forecast }),
  });
}

// ── Change Orders ───────────────────────────────────────────────────

export async function addChangeOrder(order) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.add_change_order', ...order }),
  });
}

export async function deleteChangeOrder(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.delete_change_order', id }),
  });
}

// ── Fixed Costs CRUD ────────────────────────────────────────────────

export async function fetchFixedCosts() {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=fixed_costs`);
  return data?.fixed_costs || [];
}

export async function upsertFixedCost(cost) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.upsert_fixed_cost', ...cost }),
  });
}

export async function deleteFixedCost(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.delete_fixed_cost', id }),
  });
}

// ── Tax / TGSS Config ───────────────────────────────────────────────

export async function fetchConfig(key) {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=cfo_config&key=${encodeURIComponent(key)}`);
  return data?.config?.value || null;
}

export async function saveConfig(key, value) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.save_config', key, value }),
  });
}

// ── Fiscal Calendar ─────────────────────────────────────────────────

export async function generateFiscalCalendar(year, overwrite = false) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.generate_fiscal_calendar', year, overwrite }),
  });
}

export async function fetchFiscalObligations() {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=fiscal_obligations`);
  return data?.fiscal_obligations || [];
}

export async function markFiscalPaid(id, actualAmount, paidDate) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.mark_fiscal_paid', id, actual_amount: actualAmount, paid_date: paidDate }),
  });
}

// ── Bank Labels ─────────────────────────────────────────────────────

export async function fetchBankLabels() {
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=bank_labels`);
  return data?.bank_labels || {};
}

export async function saveBankLabels(labels) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.save_bank_labels', labels }),
  });
}

// ── Reconciliation Engine ───────────────────────────────────────────

/**
 * Trigger server-side auto-reconciliation for all unmatched transactions.
 * Returns { success, processed, matched, unmatched }.
 */
export async function runReconciliation() {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.run_reconciliation' }),
  });
}

/**
 * Manually link a transaction to a specific entity.
 * @param {string} txId      - bank_transactions.id
 * @param {string} matchType - 'invoice' | 'supplier_invoice' | 'fixed_cost' | 'fiscal' | 'unmatched'
 * @param {string|null} matchId - uuid of the target entity (null to mark unmatched)
 */
export async function manualMatch(txId, matchType, matchId) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.manual_match', id: txId, match_type: matchType, match_id: matchId }),
  });
}

/**
 * Fetch unmatched transactions grouped by gap type.
 * Returns { unmatched_inflows, unmatched_outflows, ar_overdue, total_unmatched }.
 */
export async function fetchUnmatched() {
  return apiFetch(`${CFO}?action=cfo.entity&entity=unmatched_summary`);
}

// ── Cash Flow Timeline ───────────────────────────────────────────────────

/**
 * Fetch cash flow data for a given period.
 * @param {string} period - 'week' | 'month' | 'trimester' | '6months' | 'year' | 'custom'
 * @param {string|null} startDate - ISO date (required for 'custom')
 * @param {string|null} endDate   - ISO date (required for 'custom')
 * @returns {Promise<Object>} Cash flow data including bars, balance_line, fiscal_markers, statement
 */
export async function fetchCashFlowData(period = 'month', startDate = null, endDate = null) {
  const params = new URLSearchParams({ action: 'cfo.entity', entity: 'cash_flow', period });
  if (startDate) params.set('startDate', startDate);
  if (endDate) params.set('endDate', endDate);
  return apiFetch(`${CFO}?${params.toString()}`);
}

// ── KPI Dashboard ────────────────────────────────────────────────────

/**
 * Fetch all 5 cash health KPIs in a single call.
 * Returns {
 *   dso: { days, sample_count },
 *   dpo: { days, sample_count },
 *   ccc: { days },
 *   cash_runway: { months, bank_balance, tax_provisions_due, monthly_burn },
 *   true_available_cash: { amount, bank_balance, iva_owed, is_provision, upcoming_tgss },
 *   calculated_at: string,
 * }
 */
export async function fetchKPIDashboard() {
  return apiFetch(`${CFO}?action=cfo.entity&entity=kpi_dashboard`);
}

// ── Actual Payments ──────────────────────────────────────────────────

/**
 * Fetch actual payment records.
 * @param {string|null} fixedCostId - optional filter by fixed_cost_id
 * @returns {Promise<Array>} actual_payments array
 */
export async function fetchActualPayments(fixedCostId) {
  const params = new URLSearchParams({ action: 'cfo.entity', entity: 'actual_payments' });
  if (fixedCostId) params.set('fixed_cost_id', fixedCostId);
  const data = await apiFetch(`${CFO}?${params.toString()}`);
  return data?.actual_payments || [];
}

/**
 * Upsert an actual payment record (insert or update by fixed_cost_id + period).
 * @param {Object} payment - { fixed_cost_id, period, actual_amount, paid_date, breakdown_json?, notes? }
 */
export async function upsertActualPayment(payment) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.upsert_actual_payment', ...payment }),
  });
}

/**
 * Delete an actual payment record.
 * @param {string} id - actual_payments.id uuid
 */
export async function deleteActualPayment(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.delete_actual_payment', id }),
  });
}

// ── Tax Calculations ─────────────────────────────────────────────────

/**
 * Fetch quarterly IVA reconciliation for a given year.
 * Returns {
 *   year, iva_rate, quarters, ytd,
 *   missing_supplier_invoices_count, missing_deductible_iva_estimate
 * }
 * @param {number} [year] - defaults to current year
 */
export async function fetchIVAReconciliation(year) {
  const y = year || new Date().getFullYear();
  return apiFetch(`${CFO}?action=cfo.entity&entity=iva_reconciliation&year=${y}`);
}

/**
 * Fetch YTD IS (corporate tax) running estimate for a given year.
 * Returns {
 *   year, is_rate, ytd_revenue, ytd_costs_invoiced, ytd_fixed_costs,
 *   ytd_total_costs, taxable_profit, provision
 * }
 * @param {number} [year] - defaults to current year
 */
export async function fetchISEstimate(year) {
  const y = year || new Date().getFullYear();
  return apiFetch(`${CFO}?action=cfo.entity&entity=is_estimate&year=${y}`);
}

/**
 * Fetch per-project true margin breakdown for a given year.
 * Returns {
 *   year, is_rate, total_revenue, total_monthly_fixed_costs,
 *   projects: [{ project_id, name, revenue, costs, gross_margin, gross_margin_pct,
 *                overhead_allocation, tax_provision, true_margin, true_margin_pct,
 *                true_margin_color }]
 * }
 * @param {number} [year] - defaults to current year
 */
export async function fetchTrueMargin(year) {
  const y = year || new Date().getFullYear();
  return apiFetch(`${CFO}?action=cfo.entity&entity=true_margin&year=${y}`);
}

// ── C1: Activity Feed ────────────────────────────────────────────────

export async function fetchActivityFeed(since) {
  const params = since ? `&since=${since}` : '';
  const data = await apiFetch(`${CFO}?action=cfo.entity&entity=activity_feed${params}`);
  return data?.activities || [];
}

// ── C4: Pending Decisions ────────────────────────────────────────────

export async function fetchPendingDecisions() {
  return apiFetch(`${CFO}?action=cfo.entity&entity=pending_decisions`);
}

// ── Invoice Intake & Review ─────────────────────────────────────────

export async function fetchPendingInvoices(filter = 'all') {
  const params = new URLSearchParams({ action: 'cfo.list', entity: 'supplier_invoices' });
  const res = await apiFetch(`${CFO}?${params}`);
  const invoices = res.supplier_invoices || [];
  if (filter === 'all') return invoices;
  return invoices.filter(inv => inv.review_status === filter);
}

export async function uploadInvoiceFiles(files, projectId = null) {
  const filePayloads = await Promise.all(
    Array.from(files).map(async (file) => {
      const buffer = await file.arrayBuffer();
      // TICKET-1217: chunked — spreading a ~600 KB buffer overflowed the call stack.
      const base64 = bytesToBase64(new Uint8Array(buffer));
      return { name: file.name, mime_type: file.type, data: base64 };
    })
  );

  const body = { source: 'upload', files: filePayloads };
  if (projectId) body.project_id = projectId;

  // TICKET-1116: extraction runs up to the endpoint's maxDuration (120 s); the 15 s
  // default reported "timed out" while the invoice was still being filed. noRetry:
  // a resend after a dropped connection would re-run a paid extraction.
  return apiFetch(`${API_BASE}/invoice-intake`, {
    method: 'POST',
    body: JSON.stringify(body),
    timeout: 120000,
    noRetry: true,
  });
}

export async function confirmInvoice(invoiceData) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.confirm_invoice', ...invoiceData }),
  });
}

export async function rejectInvoice(id) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({
      action: 'cfo.upsert_supplier_invoice',
      id,
      review_status: 'rejected',
    }),
  });
}

// Server-side supplier-invoice ingest from Gmail. Pass ONE of:
//   • {forward_to} — the gestoría address; parses the CEO's forward-stream (the
//     canonical complete cost ledger). Drains the backlog `limit` NEW at a time:
//     loop while the response has more_available:true (dedup makes it resumable).
//   • {sender} (e.g. 'jnb.es') — newest PDFs from one supplier.
//   • explicit {message_ids}.
// dry_run=true returns the extracted line items without writing. Authed by the
// caller's CEO session — no secret leaves Vercel.
export async function ingestFromGmail({ sender, message_ids, forward_to, limit, newer_than, reparse, dry_run = true } = {}) {
  return apiFetch(CFO, {
    method: 'POST',
    timeout: 90000, // extraction is ~15s/invoice; wait for the batch instead of the ~15s default abort
    body: JSON.stringify({ action: 'supplier_invoice.ingest_from_gmail', sender, message_ids, forward_to, limit, newer_than, reparse, dry_run }),
  });
}

// Backfill the original PDF for already-ingested invoices missing one (file_id NULL).
// Cheap — re-fetches + stores the PDF from each row's gmail_message_id, no re-extraction.
// Returns { ok, scanned, backfilled, skipped, errors }.
export async function backfillInvoicePdfs({ limit } = {}) {
  return apiFetch(CFO, {
    method: 'POST',
    timeout: 90000, // a batch re-fetches N attachments from Gmail
    body: JSON.stringify({ action: 'supplier_invoice.backfill_pdfs', limit }),
  });
}

/**
 * Reject an invoice from the Review Queue (2026-04-21 bugfix).
 * Replaces the broken upsert_supplier_invoice overload that required
 * amount_eur and dropped review_status. Uses dedicated cfo.reject_invoice.
 */
export async function rejectInvoiceById(id, notes) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({ action: 'cfo.reject_invoice', id, notes }),
  });
}

// Slice 3: fetch the month/week archive payload — invoice rows + signed PDF URLs +
// a CSV manifest. The client fetches each URL and zips them with the manifest.
export async function exportInvoicesRange({ start, end } = {}) {
  const params = new URLSearchParams({ action: 'cfo.export_invoices_range', start, end });
  return apiFetch(`${CFO}?${params.toString()}`);
}

/**
 * Request a short-lived signed URL to the original invoice PDF.
 * Pass any ONE of { file_id, supplier_invoice_id, invoice_id }.
 * Returns { url, original_filename, expires_in }.
 */
export async function fetchInvoiceFileUrl({ file_id, supplier_invoice_id, invoice_id } = {}) {
  const params = new URLSearchParams({ action: 'cfo.invoice_file_url' });
  if (file_id) params.set('file_id', file_id);
  if (supplier_invoice_id) params.set('supplier_invoice_id', supplier_invoice_id);
  if (invoice_id) params.set('invoice_id', invoice_id);
  return apiFetch(`${CFO}?${params.toString()}`);
}

// ── Close-Out + Chase List (Phase 2 finance-wiring) ─────────────────

export async function fetchCloseOutSummary() {
  const data = await apiFetch(`${CFO}?action=cfo.close_out_summary`);
  return data?.summary || null;
}

export async function fetchChaseList() {
  const data = await apiFetch(`${CFO}?action=cfo.chase_list`);
  return data?.chase_list || [];
}

/**
 * Paid vs outstanding per emitted invoice, rolled up across every bank
 * transaction linked to it (TICKET-836) — so an invoice a client settled in two
 * transfers shows as part-paid instead of unpaid.
 *
 * @param {object} [opts]
 * @param {'unpaid'|'partial'|'paid'|'overpaid'|'credit_note'} [opts.state]
 * @param {boolean} [opts.openOnly] only invoices that still owe money
 * @returns {Promise<{rows: object[], totals: object}>}
 */
export async function fetchInvoicePayments({ state, openOnly } = {}) {
  const params = new URLSearchParams({ action: 'cfo.invoice_payments' });
  if (state) params.set('state', state);
  if (openOnly) params.set('open', '1');
  const data = await apiFetch(`${CFO}?${params.toString()}`);
  return { rows: data?.invoice_payments || [], totals: data?.totals || null };
}

/**
 * Append a chase action for a target row in the chase list.
 * @param {object} params
 * @param {'supplier_invoice'|'invoice'|'bank_transaction'} params.target_type
 * @param {string} params.target_id — UUID of the target row
 * @param {'forwarded_to_finutive'|'chased_supplier'|'uploaded_pdf'|'marked_no_invoice'|'marked_resolved'} params.action_type
 * @param {string} [params.note]
 */
export async function markChaseAction({ target_type, target_id, action_type, note }) {
  return apiFetch(CFO, {
    method: 'POST',
    body: JSON.stringify({
      action: 'cfo.chase_mark_action',
      target_type,
      target_id,
      action_type,
      note,
    }),
  });
}

// ── Supplier Fiscal Treatments (Phase 3 finance-wiring) ─────────────

/**
 * Lookup the VAT/fiscal rule for a supplier by name or sender email.
 * Returns the row (including treatment, country, vat_id, notes) or null.
 */
export async function fetchSupplierFiscalTreatment({ supplier_name, sender_email } = {}) {
  const params = new URLSearchParams({ action: 'cfo.supplier_fiscal_treatment' });
  if (supplier_name) params.set('supplier_name', supplier_name);
  if (sender_email) params.set('sender_email', sender_email);
  const data = await apiFetch(`${CFO}?${params.toString()}`);
  return data?.treatment || null;
}

// ── Accountant Handoff (visibility + one-click send) ────────────────

/**
 * The received-vs-forwarded snapshot + unsent worklist.
 * Returns { summary: { received_total, pending_review, confirmed_unsent,
 *   confirmed_unsent_no_pdf, sent_total, last_forwarded_at }, unsent: [...] }.
 */
export async function fetchAccountantReconciliation() {
  return apiFetch(`${CFO}?action=cfo.accountant_reconciliation`);
}

/**
 * Manual "Send pack now" — sends every confirmed, unsent, PDF-attached invoice
 * to the accountant immediately (still honours the kill switch).
 * Returns { ok, sent_count, failed_count, emails_sent }.
 */
export async function sendAccountantPackNow() {
  return apiFetch(CFO, {
    method: 'POST',
    timeout: 60000,
    body: JSON.stringify({ action: 'cfo.send_accountant_pack' }),
  });
}

// ── Cierre (finance close, TICKET-1193) ────────────────────────────

/** Import a Finutive Emitidas/Recibidas sheet (header row + data rows). Returns { imported, skipped_duplicates }. */
export async function importFinutive(kind, rows) {
  return apiFetch(CFO, {
    method: 'POST',
    timeout: 60000,
    body: JSON.stringify({ action: 'cfo.import_finutive', kind, rows }),
  });
}

/** Receipts of the year vs Finutive invoices vs quotes. Returns { year, rows, totals }. */
export async function fetchSalesRegister(year, { purchases = false } = {}) {
  // TICKET-1336: purchases=1 adds the purchase side (Finutive recibidas x bank, ~8 s).
  return apiFetch(`${CFO}?action=cfo.sales_register&year=${encodeURIComponent(year)}${purchases ? '&purchases=1' : ''}`);
}

/** Persist one row's declare_as / service_type / project_fk. */
export async function updateSalesRegisterRow(body) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.sales_register_update', ...body }) });
}

/** Stamp the lump-sum rows sent to Finutive for a quarter. Returns { marked }. */
export async function markSalesDeclared(bankTxnIds, period) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.sales_register_mark_declared', bank_txn_ids: bankTxnIds, period }) });
}

/** Close checklist for a period ('2026-Q3' or '2026-10'). Returns { period, items }. */
export async function fetchCloseChecklist(period) {
  return apiFetch(`${CFO}?action=cfo.close_checklist&period=${encodeURIComponent(period)}`);
}

/** TICKET-1196: open points for a period. Returns { period, computed_at, items, kpis }. */
export async function fetchCloseOpenPoints(period) {
  return apiFetch(`${CFO}?action=cfo.close_open_points&period=${encodeURIComponent(period)}`);
}

/** Tick / untick one checklist item: { period, item_key, done, note? }. */
export async function toggleCloseChecklist(body) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.close_checklist_toggle', ...body }) });
}

/** Save the comment on one open point / checklist row (TICKET-1197). Body: { period, item_key, note }. */
export async function saveCloseItemNote(body) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.close_item_note', ...body }) });
}

/** TICKET-1328: 2026 monthly cash result from the categorized bank lines. */
export function fetchCashPnl() {
  return apiFetch(`${CFO}?action=cfo.cash_pnl`);
}

/** TICKET-1350: AR + AP aging (0-30 / 31-60 / 61-90 / 90+), open year + closed years group. */
export function fetchAging() {
  return apiFetch(`${CFO}?action=cfo.aging`);
}

/** TICKET-1351: 2026 P&L by vendor (Finutive base) and by cost category, with coverage. */
export function fetchPlBreakdown() {
  return apiFetch(`${CFO}?action=cfo.pl_breakdown`);
}

/** TICKET-1354: untagged purchase invoices with candidate projects (supplier's last project preselected). */
export function fetchTagQueue() {
  return apiFetch(`${CFO}?action=cfo.tag_queue`);
}

/** TICKET-1354: one tap = one invoice tagged to one project. */
/** TICKET-1352: open 2026 sales invoices with next chase action + draft in the CEO's voice. */
export function fetchArChase() {
  return apiFetch(`${CFO}?action=cfo.ar_chase`);
}

/** TICKET-1352: record 'chased_client' or 'payment_promised' (note = YYYY-MM-DD). Nothing is sent. */
export function recordChaseAction(invoiceId, actionType, note) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.chase_action', invoice_id: invoiceId, action_type: actionType, note }) });
}

export function tagProject(supplierInvoiceId, projectId) {
  return apiFetch(CFO, { method: 'POST', body: JSON.stringify({ action: 'cfo.tag_project', supplier_invoice_id: supplierInvoiceId, project_id: projectId }) });
}

/** TICKET-1332: Real Treasury over ALL bank lines (closing balance per bank + match counts). */
export function fetchTreasurySummary() {
  return apiFetch(`${CFO}?action=cfo.treasury_summary`);
}

/** TICKET-1333: 30/60-day cash forecast from open 2026 invoices + signed quotes not yet invoiced. */
export function fetchCashForecast() {
  return apiFetch(`${CFO}?action=cfo.cash_forecast`);
}
